import Auth from '../auth'

import { setSentryUserContext, clearSentryUserContext } from '../../sentry/userContext'
import { logError } from '../../utils/logger'

// services/auth.js only imports this module back via a dynamic import() inside logout(),
// which resolves after load and never closes a real load-time cycle; import/no-cycle's
// default config doesn't distinguish static from dynamic imports.
// eslint-disable-next-line import/no-cycle
import { loginService, deleteAllData, getAndSetDataFromToken } from '../services/auth'

import config from '../../config'
import { msalInstance } from '../../config/msalConfig'
import { beginLogout, endLogout } from './logoutState'

const { IS_TEST_E2E } = config

const loginViaAuth0 = (auth0Client, client, popup) =>
  loginService(auth0Client, client, false, popup)

const loginViaToken = async client => {
  const token = window.localStorage.getItem('cypressAccessToken')
  if (!token) {
    throw new Error(
      'E2E login requires localStorage.cypressAccessToken (set by Playwright global-setup or makeToken.js).',
    )
  }
  const { person, picture } = await getAndSetDataFromToken(token, client)
  return { ...person, picture }
}

const selectLoginStrategy = (auth0Client, client, popup) =>
  IS_TEST_E2E ? loginViaToken(client) : loginViaAuth0(auth0Client, client, popup)

const applyLoginSuccess = (dispatch, user) => {
  dispatch(Auth.loginSuccess(user, false))
}

const applyLoginFailure = (dispatch, error) => {
  logError(error, 'redux > thunks > auth > loginThunk > catch: ')
  dispatch(Auth.loginFail(error.message))
}

export const loginThunk = (auth0Client, client, navigate, popup) => async dispatch => {
  dispatch(Auth.loginRequest())
  try {
    const user = await selectLoginStrategy(auth0Client, client, popup)
    if (user) {
      applyLoginSuccess(dispatch, user)
    }
    await setSentryUserContext(user, { client })
    navigate('/')
  } catch (error) {
    applyLoginFailure(dispatch, error)
  }
}

export const logoutThunk = () => async dispatch => {
  if (!beginLogout()) return
  try {
    dispatch(Auth.logoutRequest())
    await deleteAllData({ preserveMsalCache: true })
    clearSentryUserContext()
    dispatch(Auth.logoutSuccess())
    try {
      await msalInstance.handleRedirectPromise()
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      })
    } catch (e) {
      if (e?.errorCode === 'interaction_in_progress') {
        window.location.assign(window.location.origin)
        return
      }
      logError(e, 'redux > thunks > auth > logoutThunk > msal logoutRedirect')
    }
  } finally {
    endLogout()
  }
}
